import AccountStore from "../stores/AccountStore"
import AuthenticationStore from "../stores/AuthenticationStore"
import { EnumValues } from "enum-values"
import { EExceptionUITypes, EExceptionState, IPunchException, EExceptionType } from "../models"
import * as utils from "../helpers/utils"
import * as permissonHelper from "./permissionHelper"

export function getExceptionState(punchException: IPunchException): EExceptionState {
    return punchException.Approved ?
        EExceptionState.Approved :
        punchException.Rejected ?
            EExceptionState.Rejected :
            EExceptionState.Review
}

export function getExceptionFilterAllItems(): EExceptionUITypes[] {
    const allFilterItems: EExceptionUITypes[] = EnumValues.getValues(EExceptionUITypes)
    if (!AccountStore.getEmployeeApproveTimecardEnabled() || AccountStore.getDisableEmployeeApprovalException()) {
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.NoEmpApprove), 1)
    }

    if (AccountStore.getDisableSupervisorApprovalException()) {
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.NoSupApprove), 1)
    }

    if (!AccountStore.getScheduleEnabled()) {
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleEarlyInPunch), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleEarlyOutPunch), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleLateInPunch), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleLateOutPunch), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleLongBreak), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleMissingBreak), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleMissingInPunch), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.ScheduleNoShift), 1)
    }
    if (!AccountStore.getPunchQuestionEnabled()) {
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.QuestionException), 1)
    }
    if (!AccountStore.getAccrualsTimeoffEnabled() || !permissonHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.EditLeaveTracking)) {
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.Timeoff), 1)
    }

    allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.DeviceStatus), 1)

    if (!permissonHelper.hasPermission(AuthenticationStore.Permissions.PunchRecords.ViewRecords)) {
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.SuspensePunch), 1)
        allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.TimeFraud), 1)
    }

    allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.PunchNote), 1)
    allFilterItems.splice(allFilterItems.indexOf(EExceptionUITypes.CAMealBreakException), 1)
    return allFilterItems
}

export function getExceptionFilterShowItemsByStorage(): EExceptionUITypes[] {
    const localStorageKey = "ShowExceptionFilterItems"
    const storedShowFilterItemsStr = utils.getCookie(localStorageKey)
    const showFilterItems =
        storedShowFilterItemsStr ?
            storedShowFilterItemsStr === "none" ?
                [] :
                (storedShowFilterItemsStr.split(",") as EExceptionUITypes[])
                    .filter(item => AccountStore.getScheduleEnabled() ? true : !item.includes("schedule")) :
            getExceptionFilterAllItems().slice()
    return showFilterItems
}

export function getSelectedExceptionFilterTypesByStorage(localStorageKey: string): EExceptionUITypes[] {
    const showItems = getExceptionFilterShowItemsByStorage()
    const storedExceptionFilterTypesStr = utils.getCookie(localStorageKey)
    const selectedExceptionFilterTypes =
        storedExceptionFilterTypesStr ?
            storedExceptionFilterTypesStr !== "none" ?
                (storedExceptionFilterTypesStr.split(",") as EExceptionUITypes[])
                    .filter(item => AccountStore.getScheduleEnabled() ? true : !item.includes("schedule"))
                    .filter(item => showItems.includes(item)) :
                [] :
            [EExceptionUITypes.MissPunch]
    return selectedExceptionFilterTypes
}

export function getExceptionTypes(punchSetFilterTypes: EExceptionUITypes[]): EExceptionType[] {
    const exceptionTypes: EExceptionType[] = []
    punchSetFilterTypes.forEach(item => {
        switch (item) {
            case EExceptionUITypes.BioException:
                exceptionTypes.push(EExceptionType.BIOMETRIC)
                break
            case EExceptionUITypes.LocException:
                exceptionTypes.push(EExceptionType.LOCATION)
                exceptionTypes.push(EExceptionType.MICRO_LOCATION)
                exceptionTypes.push(EExceptionType.NO_DEVICE_LOCATION)
                exceptionTypes.push(EExceptionType.NO_LOCATION_OR_MICROLOCATION)
                break
            case EExceptionUITypes.MissPunch:
                exceptionTypes.push(EExceptionType.MISSING_PUNCH)
                break
            case EExceptionUITypes.NoEmpApprove:
                if (!AccountStore.getDisableEmployeeApprovalException()) exceptionTypes.push(EExceptionType.MISSING_EMPLOYEE_APPROVAL)
                break
            case EExceptionUITypes.NoSupApprove:
                if (!AccountStore.getDisableSupervisorApprovalException()) exceptionTypes.push(EExceptionType.MISSING_SUPERVISOR_APPROVAL)
                break
            case EExceptionUITypes.ScheduleEarlyInPunch:
                exceptionTypes.push(EExceptionType.EARLY_IN_PUNCH)
                break
            case EExceptionUITypes.ScheduleEarlyOutPunch:
                exceptionTypes.push(EExceptionType.EARLY_OUT_PUNCH)
                break
            case EExceptionUITypes.ScheduleLateInPunch:
                exceptionTypes.push(EExceptionType.LATE_IN_PUNCH)
                break
            case EExceptionUITypes.ScheduleLateOutPunch:
                exceptionTypes.push(EExceptionType.LATE_OUT_PUNCH)
                break
            case EExceptionUITypes.ScheduleLongBreak:
                exceptionTypes.push(EExceptionType.LONG_BREAK)
                break
            case EExceptionUITypes.ScheduleMissingBreak:
                exceptionTypes.push(EExceptionType.MISSING_BREAK)
                break
            case EExceptionUITypes.ScheduleMissingInPunch:
                exceptionTypes.push(EExceptionType.MISSING_IN_PUNCH)
                break
            case EExceptionUITypes.ScheduleNoShift:
                exceptionTypes.push(EExceptionType.NO_SHIFT)
                break
            case EExceptionUITypes.QuestionException:
                exceptionTypes.push(EExceptionType.QUESTION_EXCEPTION)
                break
            case EExceptionUITypes.Timeoff:
                exceptionTypes.push(EExceptionType.TIMEOFF)
                break
            case EExceptionUITypes.SuspensePunch:
                exceptionTypes.push(EExceptionType.SUSPENSE_PUNCH)
                break
            case EExceptionUITypes.TimeFraud:
                exceptionTypes.push(EExceptionType.TIME_FRAUD)
                break
        }
    })
    return exceptionTypes
}

export function getValidExceptionTypesForFilterTimecards(filterTypes: EExceptionUITypes[]): EExceptionUITypes[] {
    const allValidExceptionTypesForFilterTimecards = [EExceptionUITypes.MissPunch, EExceptionUITypes.NoEmpApprove, EExceptionUITypes.NoSupApprove, EExceptionUITypes.BioException, EExceptionUITypes.LocException]
    return filterTypes.filter(i => allValidExceptionTypesForFilterTimecards.includes(i))
}